import React from "react";
import Layouts from "../Layouts";
import { Divider, Input } from "antd";
import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  LockOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

const ChangePassword = () => {
  return (
    <Layouts>
      <main className="min-h-screen w-full bg-[#FAFAF7]">

        {/* =====================================================
            PAGE WRAPPER
        ====================================================== */}
        <div className="
          w-full
          max-w-6xl
          mx-auto
          px-3
          sm:px-5
          md:px-8
          lg:px-10
          py-5
          sm:py-8
          md:py-12
          lg:py-16
        ">

          {/* =====================================================
              MAIN CARD
          ====================================================== */}
          <div className="
            w-full
            bg-white
            rounded-xl
            sm:rounded-2xl
            lg:rounded-3xl
            border border-[#EEE8DA]
            shadow-[0_8px_30px_rgba(0,0,0,0.05)]
            overflow-hidden
          ">

            {/* =====================================================
                HEADER
            ====================================================== */}
            <div className="
              px-4
              sm:px-6
              md:px-8
              lg:px-10
              pt-5
              sm:pt-7
              md:pt-9
              lg:pt-10
            ">

              {/* Header Content */}
              <div className="flex items-center gap-3 sm:gap-4">

                {/* Lock Icon */}
                <div className="
                  w-9
                  h-9
                  sm:w-11
                  sm:h-11
                  md:w-12
                  md:h-12
                  rounded-lg
                  sm:rounded-xl
                  bg-[#FFF4D6]
                  flex
                  items-center
                  justify-center
                  flex-shrink-0
                ">
                  <LockOutlined className="
                    text-[#A17A17]
                    text-base
                    sm:text-lg
                    md:text-xl
                  " />
                </div>


                {/* Title */}
                <div className="min-w-0 flex-1">

                  <p className="
                    text-[9px]
                    sm:text-[10px]
                    md:text-xs
                    uppercase
                    tracking-[0.15em]
                    sm:tracking-[0.2em]
                    font-bold
                    text-[#A17A17]
                  ">
                    Account Security
                  </p>

                  <h1 className="
                    mt-0.5
                    text-xl
                    sm:text-2xl
                    md:text-3xl
                    lg:text-4xl
                    font-serif
                    font-bold
                    text-[#3F342A]
                    leading-tight
                    break-words
                  ">
                    Change Your Password
                  </h1>

                </div>

              </div>


              {/* Description */}
              <p className="
                mt-3
                sm:mt-4
                text-xs
                sm:text-sm
                md:text-base
                text-gray-500
                leading-5
                sm:leading-6
                max-w-2xl
              ">
                Update your password regularly to keep your account secure.
              </p>


              <Divider className="
                my-5
                sm:my-6
                md:my-8
              " />

            </div>


            {/* =====================================================
                CONTENT
            ====================================================== */}
            <div className="
              px-4
              sm:px-6
              md:px-8
              lg:px-10
              pb-5
              sm:pb-7
              md:pb-9
              lg:pb-10
            ">

              <div className="
                grid
                grid-cols-1
                lg:grid-cols-2
                gap-6
                sm:gap-7
                md:gap-8
                lg:gap-12
                items-start
              ">


                {/* =================================================
                    PASSWORD FORM
                ================================================== */}
                <section className="w-full min-w-0">

                  <div className="space-y-4 sm:space-y-5 md:space-y-6">

                    {/* Old Password */}
                    <div className="w-full">

                      <label className="
                        block
                        mb-1.5
                        sm:mb-2
                        text-xs
                        sm:text-sm
                        md:text-base
                        font-semibold
                        text-[#3F342A]
                      ">
                        Old Password
                        <span className="text-red-500 ml-1">*</span>
                      </label>

                      <Input.Password
                        size="large"
                        prefix={
                          <LockOutlined className="
                            text-gray-400
                            mr-1
                            text-sm
                            sm:text-base
                          " />
                        }
                        placeholder="Enter your old password"
                        iconRender={(visible) =>
                          visible ? (
                            <EyeTwoTone />
                          ) : (
                            <EyeInvisibleOutlined />
                          )
                        }
                        className="
                          w-full
                          rounded-lg
                          sm:rounded-xl
                        "
                      />

                    </div>


                    {/* New Password */}
                    <div className="w-full">

                      <label className="
                        block
                        mb-1.5
                        sm:mb-2
                        text-xs
                        sm:text-sm
                        md:text-base
                        font-semibold
                        text-[#3F342A]
                      ">
                        New Password
                        <span className="text-red-500 ml-1">*</span>
                      </label>

                      <Input.Password
                        size="large"
                        prefix={
                          <LockOutlined className="
                            text-gray-400
                            mr-1
                            text-sm
                            sm:text-base
                          " />
                        }
                        placeholder="Enter your new password"
                        iconRender={(visible) =>
                          visible ? (
                            <EyeTwoTone />
                          ) : (
                            <EyeInvisibleOutlined />
                          )
                        }
                        className="
                          w-full
                          rounded-lg
                          sm:rounded-xl
                        "
                      />

                    </div>


                    {/* Confirm Password */}
                    <div className="w-full">

                      <label className="
                        block
                        mb-1.5
                        sm:mb-2
                        text-xs
                        sm:text-sm
                        md:text-base
                        font-semibold
                        text-[#3F342A]
                      ">
                        Confirm Password
                        <span className="text-red-500 ml-1">*</span>
                      </label>

                      <Input.Password
                        size="large"
                        prefix={
                          <LockOutlined className="
                            text-gray-400
                            mr-1
                            text-sm
                            sm:text-base
                          " />
                        }
                        placeholder="Re-enter your new password"
                        iconRender={(visible) =>
                          visible ? (
                            <EyeTwoTone />
                          ) : (
                            <EyeInvisibleOutlined />
                          )
                        }
                        className="
                          w-full
                          rounded-lg
                          sm:rounded-xl
                        "
                      />

                    </div>


                    {/* Change Password Button */}
                    <button
                      type="button"
                      className="
                        w-full
                        mt-1
                        sm:mt-2
                        py-2.5
                        sm:py-3
                        md:py-3.5
                        px-4
                        rounded-lg
                        sm:rounded-xl
                        bg-[#A17A17]
                        hover:bg-[#8D6714]
                        active:scale-[0.99]
                        text-white
                        text-xs
                        sm:text-sm
                        md:text-base
                        font-semibold
                        shadow-sm
                        hover:shadow-md
                        transition-all
                        duration-200
                      "
                    >
                      Change Password
                    </button>

                  </div>

                </section>


                {/* =================================================
                    PASSWORD REQUIREMENTS
                ================================================== */}
                <section className="w-full min-w-0">

                  <div className="
                    w-full
                    rounded-xl
                    sm:rounded-2xl
                    bg-[#FCFAF4]
                    border border-[#E9DFC7]
                    p-4
                    sm:p-5
                    md:p-6
                    lg:p-7
                  ">

                    {/* Requirements Header */}
                    <div className="flex items-start gap-2.5 sm:gap-3">

                      <div className="
                        w-8
                        h-8
                        sm:w-9
                        sm:h-9
                        md:w-10
                        md:h-10
                        rounded-lg
                        bg-[#FFF0C0]
                        flex
                        items-center
                        justify-center
                        flex-shrink-0
                      ">
                        <CheckCircleOutlined className="
                          text-[#A17A17]
                          text-sm
                          sm:text-base
                        " />
                      </div>


                      <div className="min-w-0">

                        <h2 className="
                          text-base
                          sm:text-lg
                          md:text-xl
                          font-bold
                          text-[#3F342A]
                          leading-tight
                        ">
                          Password Requirements
                        </h2>

                        <p className="
                          text-[11px]
                          sm:text-xs
                          md:text-sm
                          text-gray-500
                          mt-1
                          leading-5
                        ">
                          Make sure your new password meets all the
                          requirements below.
                        </p>

                      </div>

                    </div>


                    <Divider className="
                      my-4
                      sm:my-5
                    " />


                    {/* Requirements List */}
                    <div className="space-y-3 sm:space-y-4">

                      {/* Requirement 1 */}
                      <div className="flex items-start gap-2.5 sm:gap-3">

                        <CheckCircleOutlined className="
                          text-[#A17A17]
                          mt-0.5
                          flex-shrink-0
                          text-sm
                          sm:text-base
                        " />

                        <p className="
                          text-xs
                          sm:text-sm
                          md:text-base
                          text-gray-600
                          leading-5
                          sm:leading-6
                        ">
                          Must contain a minimum of{" "}
                          <strong className="text-gray-800">
                            8
                          </strong>{" "}
                          and maximum of{" "}
                          <strong className="text-gray-800">
                            16 characters
                          </strong>.
                        </p>

                      </div>


                      {/* Requirement 2 */}
                      <div className="flex items-start gap-2.5 sm:gap-3">

                        <CheckCircleOutlined className="
                          text-[#A17A17]
                          mt-0.5
                          flex-shrink-0
                          text-sm
                          sm:text-base
                        " />

                        <p className="
                          text-xs
                          sm:text-sm
                          md:text-base
                          text-gray-600
                          leading-5
                          sm:leading-6
                        ">
                          Must contain at least one{" "}
                          <strong className="text-gray-800">
                            uppercase letter
                          </strong>{" "}
                          and one{" "}
                          <strong className="text-gray-800">
                            numeral
                          </strong>.
                        </p>

                      </div>


                      {/* Requirement 3 */}
                      <div className="flex items-start gap-2.5 sm:gap-3">

                        <CheckCircleOutlined className="
                          text-[#A17A17]
                          mt-0.5
                          flex-shrink-0
                          text-sm
                          sm:text-base
                        " />

                        <p className="
                          text-xs
                          sm:text-sm
                          md:text-base
                          text-gray-600
                          leading-5
                          sm:leading-6
                        ">
                          Must contain at least one{" "}
                          <strong className="text-gray-800">
                            special character
                          </strong>.
                        </p>

                      </div>

                    </div>


                    {/* Security Tip */}
                    <div className="
                      mt-5
                      sm:mt-6
                      p-3
                      sm:p-4
                      rounded-lg
                      sm:rounded-xl
                      bg-white
                      border border-[#EEE5D4]
                    ">

                      <p className="
                        text-[11px]
                        sm:text-xs
                        md:text-sm
                        text-gray-500
                        leading-5
                      ">
                        <strong className="text-[#A17A17]">
                          Security tip:
                        </strong>{" "}
                        Avoid using easily guessed information such as your
                        name, phone number, or date of birth.
                      </p>

                    </div>

                  </div>

                </section>

              </div>

            </div>

          </div>

        </div>

      </main>
    </Layouts>
  );
};

export default ChangePassword;
